import { Injectable, NgZone, inject } from '@angular/core';
import { PreloadingStrategy, Route } from '@angular/router';
import { Observable, from, switchMap } from 'rxjs';

/**
 * Pré-carrega as telas lazy SEM disputar a carga inicial: começa alguns segundos depois do boot e
 * carrega uma tela por vez, só quando o navegador está ocioso. Com o PreloadAllModules as ~80 telas
 * eram baixadas e executadas juntas logo depois da primeira tela, e num PC fraco a primeira tela
 * travava por segundos. O primeiro clique em cada item do menu continua sem esperar o download.
 */
@Injectable({ providedIn: 'root' })
export class IdlePreloadingStrategy implements PreloadingStrategy {
  /** Espera antes do primeiro pré-carregamento (a primeira tela termina de montar). */
  static readonly StartDelayMs = 3000;

  private readonly zone = inject(NgZone);
  private queue: Promise<void> | null = null;

  preload(_route: Route, load: () => Observable<unknown>): Observable<unknown> {
    // Fila: cada tela espera a anterior e mais um momento ocioso.
    const previous = this.queue ?? this.delay(IdlePreloadingStrategy.StartDelayMs);
    const turn = previous.then(() => this.idle());
    this.queue = turn;
    return from(turn).pipe(switchMap(() => load()));
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => this.zone.runOutsideAngular(() => setTimeout(resolve, ms)));
  }

  private idle(): Promise<void> {
    return new Promise((resolve) => this.zone.runOutsideAngular(() => {
      const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
      if (ric) ric(() => resolve(), { timeout: 2000 });
      else setTimeout(resolve, 200);
    }));
  }
}
